import { useState, useEffect } from 'react';
import { useAuth, API } from '../../App';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  TrendingUp, 
  Users, 
  Building2, 
  DollarSign, 
  Calendar, 
  CheckCircle, 
  Clock, 
  XCircle, 
  ChevronLeft, 
  ChevronRight, 
  X,
  BarChart3,
  AlertCircle,
  Ban,
  Download
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import DriverRidesExportDialog from './DriverRidesExportDialog';


export default function Statistics() {
  const { token, user } = useAuth();
  const canSeeRevenue = user?.section_permissions?.statistics_revenue !== false;
  const [driverStats, setDriverStats] = useState([]);
  const [partnerStats, setPartnerStats] = useState([]);
  const [excludedPartners, setExcludedPartners] = useState(new Set(['VAN', 'TAXI']));
  const [loading, setLoading] = useState(true);
  const [includeCancelled, setIncludeCancelled] = useState(false);
  const [exportDriver, setExportDriver] = useState(null);
  
  // Month/Year navigation
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedDay, setSelectedDay] = useState('');
  
  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];
  
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - 2024 + 2 }, (_, i) => 2024 + i);
  
  const getDaysInMonth = (month, year) => new Date(year, month, 0).getDate();
  const daysInMonth = getDaysInMonth(selectedMonth, selectedYear);
  const dayOptions = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  
  const goToPreviousMonth = () => {
    if (selectedMonth === 1) {
      setSelectedMonth(12);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
    setSelectedDay('');
  };
  
  const goToNextMonth = () => {
    if (selectedMonth === 12) {
      setSelectedMonth(1);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
    setSelectedDay('');
  };

  useEffect(() => {
    fetchStatistics();
  }, [selectedMonth, selectedYear, selectedDay, includeCancelled]);

  const fetchStatistics = async () => {
    try {
      const monthStr = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}`;
      const period = selectedDay ? 'day' : 'month';
      const dayStr = selectedDay ? `${monthStr}-${String(selectedDay).padStart(2, '0')}` : null;
      
      const params = { 
        period, 
        month: monthStr,
        include_cancelled: includeCancelled,
        ...(dayStr && { day: dayStr })
      };
      
      const [driversRes, partnersRes] = await Promise.all([
        axios.get(`${API}/statistics/drivers`, {
          headers: { Authorization: `Bearer ${token}` },
          params
        }),
        axios.get(`${API}/statistics/partners`, {
          headers: { Authorization: `Bearer ${token}` },
          params
        })
      ]);
      setDriverStats(driversRes.data);
      setPartnerStats(partnersRes.data);
    } catch (error) {
      console.error('Error fetching statistics:', error);
      toast.error('Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  const togglePartnerExclusion = (partnerName) => {
    setExcludedPartners(prev => {
      const newSet = new Set(prev);
      if (newSet.has(partnerName)) {
        newSet.delete(partnerName);
      } else {
        newSet.add(partnerName);
      }
      return newSet;
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const getMonthName = () => months.find(m => m.value === selectedMonth)?.label || '';

  const getDateDisplay = () => {
    if (selectedDay) {
      return `${selectedDay} ${getMonthName()} ${selectedYear}`;
    }
    return `${getMonthName()} ${selectedYear}`;
  };

  // Calculate totals
  const totalDriverRides = driverStats.reduce((sum, d) => sum + d.total_rides, 0);
  const totalDriverRevenue = driverStats.reduce((sum, d) => sum + d.total_revenue, 0);
  const filteredPartnerStats = partnerStats.filter(p => !excludedPartners.has(p.partner_name));
  const totalPartnerRevenue = filteredPartnerStats.reduce((sum, p) => sum + p.total_revenue, 0);
  const totalPartnerRides = partnerStats.reduce((sum, p) => sum + p.total_rides, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full min-h-[400px]">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-slate-300 border-t-emerald-600"></div>
          <p className="text-sm text-slate-500">Loading statistics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 bg-slate-50 min-h-screen">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2" data-testid="statistics-title">
            <BarChart3 className="h-6 w-6 text-emerald-600" />
            Statistics & Reports
          </h1>
          <p className="text-sm text-slate-500 mt-1">Performance metrics and revenue analysis</p>
        </div>
      </div>

      {/* Date Navigation Card */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-4">
            {/* Month/Year Navigation */}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={goToPreviousMonth}
                className="h-10 w-10 rounded-lg border-slate-200 hover:bg-slate-100"
                data-testid="prev-month-btn"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              
              <div className="flex items-center gap-2">
                <Select 
                  value={selectedMonth.toString()} 
                  onValueChange={(v) => { setSelectedMonth(parseInt(v)); setSelectedDay(''); }}
                >
                  <SelectTrigger className="w-[130px] h-10 rounded-lg border-slate-200" data-testid="month-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {months.map(m => (
                      <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Select 
                  value={selectedYear.toString()} 
                  onValueChange={(v) => { setSelectedYear(parseInt(v)); setSelectedDay(''); }}
                >
                  <SelectTrigger className="w-[90px] h-10 rounded-lg border-slate-200" data-testid="year-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {years.map(y => (
                      <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <Button
                variant="outline"
                size="icon"
                onClick={goToNextMonth}
                className="h-10 w-10 rounded-lg border-slate-200 hover:bg-slate-100"
                data-testid="next-month-btn"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>

            {/* Day Filter */}
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-slate-400" />
                <Label className="text-sm text-slate-600">Day:</Label>
                <Select 
                  value={selectedDay ? selectedDay.toString() : 'all'} 
                  onValueChange={(v) => setSelectedDay(v === 'all' ? '' : parseInt(v))}
                >
                  <SelectTrigger className="w-[100px] h-10 rounded-lg border-slate-200" data-testid="day-select">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All days</SelectItem>
                    {dayOptions.map(d => (
                      <SelectItem key={d} value={d.toString()}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedDay && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setSelectedDay('')}
                    className="h-8 w-8 text-slate-400 hover:text-slate-600"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>

            {/* Current Period Display */}
            <div className="bg-emerald-50 px-4 py-2 rounded-lg">
              <p className="text-sm text-emerald-700 font-medium">
                {getDateDisplay()}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm bg-gradient-to-br from-blue-500 to-blue-600">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-white/20 rounded-lg flex items-center justify-center">
                <Users className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-xs text-blue-100">Drivers</p>
                <p className="text-2xl font-bold text-white">{driverStats.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm bg-gradient-to-br from-emerald-500 to-emerald-600">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-white/20 rounded-lg flex items-center justify-center">
                <Building2 className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-xs text-emerald-100">Partners</p>
                <p className="text-2xl font-bold text-white">{partnerStats.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm bg-gradient-to-br from-violet-500 to-violet-600">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-white/20 rounded-lg flex items-center justify-center">
                <TrendingUp className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-xs text-violet-100">Total Rides</p>
                <p className="text-2xl font-bold text-white">{totalDriverRides}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm bg-gradient-to-br from-amber-500 to-amber-600">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-white/20 rounded-lg flex items-center justify-center">
                <DollarSign className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-xs text-amber-100">Revenue</p>
                <p className="text-xl font-bold text-white">{canSeeRevenue ? formatCurrency(totalDriverRevenue) : '***'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Driver Performance Section */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-3 border-b border-slate-100">
          <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <div className="h-8 w-8 bg-blue-100 rounded-lg flex items-center justify-center">
              <Users className="h-4 w-4 text-blue-600" />
            </div>
            Driver Performance
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50/80">
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Driver</th>
                  <th className="text-center px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Total</th>
                  <th className="text-center px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Completed</th>
                  <th className="text-center px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Pending</th>
                  <th className="text-center px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Cancelled</th>
                  <th className="text-right px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Revenue</th>
                  <th className="text-right px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Avg/Ride</th>
                  <th className="text-center px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider w-16">Export</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {driverStats.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-5 py-12 text-center">
                      <div className="flex flex-col items-center gap-2 text-slate-400">
                        <Users className="h-8 w-8" />
                        <p className="text-sm">No driver statistics for this period</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  driverStats.map((driver, index) => (
                    <tr key={index} className="hover:bg-slate-50/50 transition-colors">
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className="h-9 w-9 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-semibold text-sm">
                            {driver.driver_name?.charAt(0)?.toUpperCase() || 'D'}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-slate-900">{driver.driver_name}</p>
                            <p className="text-xs text-slate-400">{driver.driver_email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4 text-center">
                        <span className="inline-flex items-center justify-center h-7 min-w-[28px] px-2 bg-slate-100 rounded-full text-sm font-semibold text-slate-700">
                          {driver.total_rides}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-center">
                        <span className="inline-flex items-center gap-1 text-sm text-emerald-600">
                          <CheckCircle className="h-4 w-4" />
                          {driver.completed_rides}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-center">
                        <span className="inline-flex items-center gap-1 text-sm text-amber-600">
                          <Clock className="h-4 w-4" />
                          {driver.pending_rides}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-center">
                        <span className="inline-flex items-center gap-1 text-sm text-red-500">
                          <XCircle className="h-4 w-4" />
                          {driver.cancelled_rides}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-right">
                        <span className="text-sm font-semibold text-emerald-600">{canSeeRevenue ? formatCurrency(driver.total_revenue) : '***'}</span>
                      </td>
                      <td className="px-5 py-4 text-right">
                        <span className="text-sm text-slate-500">{canSeeRevenue ? formatCurrency(driver.average_per_ride) : '***'}</span>
                      </td>
                      <td className="px-5 py-4 text-center">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 hover:bg-amber-50 hover:text-amber-600"
                          onClick={() => setExportDriver({
                            id: driver.driver_id,
                            name: driver.driver_name,
                            year: selectedYear,
                            month: selectedMonth,
                          })}
                          data-testid={`stats-export-${driver.driver_id}`}
                          title="Export monthly rides"
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Partner Performance Section */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-3 border-b border-slate-100">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <div className="h-8 w-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                <Building2 className="h-4 w-4 text-emerald-600" />
              </div>
              Partner Performance
            </CardTitle>
            
            {/* Include Cancelled Toggle */}
            <div className="flex items-center gap-3 bg-slate-50 px-4 py-2 rounded-lg" data-testid="include-cancelled-toggle">
              <div className="flex items-center gap-2">
                <Ban className="h-4 w-4 text-slate-400" />
                <Label htmlFor="include-cancelled" className="text-sm text-slate-600 cursor-pointer">
                  Include cancelled rides in revenue
                </Label>
              </div>
              <Switch
                id="include-cancelled"
                checked={includeCancelled}
                onCheckedChange={setIncludeCancelled}
                className="data-[state=checked]:bg-emerald-500"
              />
              {includeCancelled && (
                <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full font-medium">
                  Active
                </span>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {includeCancelled && (
            <div className="mx-5 mt-4 flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertCircle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-700">
                Revenue totals now include cancelled and no-show rides. This may not reflect actual collected revenue.
              </p>
            </div>
          )}
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50/80">
                  <th className="w-12 px-5 py-3"></th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Partner</th>
                  <th className="text-center px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Total</th>
                  <th className="text-center px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Completed</th>
                  <th className="text-center px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Cancelled</th>
                  <th className="text-right px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Revenue</th>
                  <th className="text-right px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Avg/Ride</th>
                  <th className="text-right px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    {selectedDay ? 'Daily' : 'Monthly'}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {partnerStats.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-5 py-12 text-center">
                      <div className="flex flex-col items-center gap-2 text-slate-400">
                        <Building2 className="h-8 w-8" />
                        <p className="text-sm">No partner statistics for this period</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  partnerStats.map((partner, index) => {
                    const isExcluded = excludedPartners.has(partner.partner_name);
                    return (
                      <tr key={index} className={`hover:bg-slate-50/50 transition-colors ${isExcluded ? 'opacity-50' : ''}`}>
                        <td className="px-5 py-4">
                          <input
                            type="checkbox"
                            checked={!isExcluded}
                            onChange={() => togglePartnerExclusion(partner.partner_name)}
                            className="w-4 h-4 text-emerald-600 rounded border-slate-300 focus:ring-emerald-500 cursor-pointer"
                          />
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-3">
                            <div className={`h-9 w-9 rounded-full flex items-center justify-center text-white font-semibold text-sm ${
                              isExcluded ? 'bg-slate-300' : 'bg-gradient-to-br from-emerald-400 to-emerald-600'
                            }`}>
                              {(partner.partner_name || 'D').charAt(0).toUpperCase()}
                            </div>
                            <p className={`text-sm font-medium ${isExcluded ? 'text-slate-400 line-through' : 'text-slate-900'}`}>
                              {partner.partner_name || 'Direct Booking'}
                            </p>
                          </div>
                        </td>
                        <td className="px-5 py-4 text-center">
                          <span className="inline-flex items-center justify-center h-7 min-w-[28px] px-2 bg-slate-100 rounded-full text-sm font-semibold text-slate-700">
                            {partner.total_rides}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-center">
                          <span className="inline-flex items-center gap-1 text-sm text-emerald-600">
                            <CheckCircle className="h-4 w-4" />
                            {partner.completed_rides}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-center">
                          <span className="inline-flex items-center gap-1 text-sm text-red-500">
                            <XCircle className="h-4 w-4" />
                            {partner.cancelled_rides || 0}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-right">
                          <div className="flex flex-col items-end">
                            <span className="text-sm font-semibold text-emerald-600">{canSeeRevenue ? formatCurrency(partner.total_revenue) : '***'}</span>
                            {canSeeRevenue && includeCancelled && partner.cancelled_revenue > 0 && (
                              <span className="text-xs text-slate-400">
                                (incl. {formatCurrency(partner.cancelled_revenue)} cancelled)
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-5 py-4 text-right">
                          <span className="text-sm text-slate-500">{canSeeRevenue ? formatCurrency(partner.average_per_ride) : '***'}</span>
                        </td>
                        <td className="px-5 py-4 text-right">
                          <span className="text-sm font-medium text-slate-700">{canSeeRevenue ? formatCurrency(partner.period_average) : '***'}</span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Partner Summary Footer */}
          {partnerStats.length > 0 && (
            <div className="p-5 bg-gradient-to-br from-slate-50 to-slate-100 border-t border-slate-200">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white p-4 rounded-xl shadow-sm">
                  <div className="flex items-center gap-2 mb-1">
                    <DollarSign className="h-4 w-4 text-emerald-500" />
                    <p className="text-xs text-slate-500 uppercase tracking-wider font-medium">Total Partner Revenue</p>
                  </div>
                  {excludedPartners.size > 0 && (
                    <p className="text-xs text-slate-400 mb-2">
                      Excluding: {Array.from(excludedPartners).join(', ')}
                    </p>
                  )}
                  <p className="text-2xl font-bold text-emerald-600">{canSeeRevenue ? formatCurrency(totalPartnerRevenue) : '***'}</p>
                </div>
                
                <div className="bg-white p-4 rounded-xl shadow-sm">
                  <div className="flex items-center gap-2 mb-1">
                    <TrendingUp className="h-4 w-4 text-blue-500" />
                    <p className="text-xs text-slate-500 uppercase tracking-wider font-medium">Total Partner Rides</p>
                  </div>
                  <p className="text-2xl font-bold text-slate-900 mt-2">{totalPartnerRides}</p>
                </div>
                
                <div className="bg-white p-4 rounded-xl shadow-sm">
                  <div className="flex items-center gap-2 mb-1">
                    <BarChart3 className="h-4 w-4 text-violet-500" />
                    <p className="text-xs text-slate-500 uppercase tracking-wider font-medium">Overall Average</p>
                  </div>
                  {excludedPartners.size > 0 && (
                    <p className="text-xs text-slate-400 mb-2">
                      Excluding: {Array.from(excludedPartners).join(', ')}
                    </p>
                  )}
                  <p className="text-2xl font-bold text-slate-900">
                    {canSeeRevenue ? formatCurrency(
                      filteredPartnerStats.reduce((sum, p) => sum + p.total_rides, 0) > 0
                        ? totalPartnerRevenue / filteredPartnerStats.reduce((sum, p) => sum + p.total_rides, 0)
                        : 0
                    ) : '***'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      {exportDriver && (
        <DriverRidesExportDialog
          open={!!exportDriver}
          onOpenChange={(o) => { if (!o) setExportDriver(null); }}
          driverId={exportDriver.id}
          driverName={exportDriver.name}
          defaultYear={exportDriver.year}
          defaultMonth={exportDriver.month}
        />
      )}
    </div>
  );
}
